import { useState } from "react";

import type { CompetencyAssignmentJSON } from "@performyard/service-contracts";

import { useSuppressAskPerformYard } from "../../../../containers/AskPerformYard/hooks/useSuppressAskPerformYard";
import { useSuppressFeedbackButtons } from "../../../../containers/FeedbackDrawers/useSuppressFeedbackButtons";

type CompetencyAssignmentActions = {
	openPanel: "levels" | "history" | null;
	actionSelectedRow: CompetencyAssignmentJSON | null;
	isUpdateModalOpen: boolean;
	onOpenPanel: (type: "levels" | "history", row: CompetencyAssignmentJSON) => void;
	closePanel: () => void;
	onUpdateLevel: (row: CompetencyAssignmentJSON) => void;
	closeUpdateModal: () => void;
};

// Shared panel/modal interaction state for the My + Team competency pages.
const useCompetencyAssignmentActions = (): CompetencyAssignmentActions => {
	// Which panel is open (transient) vs the row whose action (panel or update modal) is active —
	// persists through close so content + data query survive PanelBase's slide-out instead of
	// blanking mid-animation. Keyed by row: opening a DIFFERENT row remounts the panels, resetting
	// their internal state (history pagination, levels accordion).
	const [openPanel, setOpenPanel] = useState<"levels" | "history" | null>(null);
	const [actionSelectedRow, setActionSelectedRow] = useState<CompetencyAssignmentJSON | null>(null);
	const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

	// Both panels stay mounted, so suppress from here rather than inside them — the floating
	// launchers sit above PanelBase and would otherwise overlap whichever panel is open.
	useSuppressAskPerformYard(openPanel !== null);
	useSuppressFeedbackButtons(openPanel !== null);

	const onOpenPanel = (type: "levels" | "history", row: CompetencyAssignmentJSON) => {
		setActionSelectedRow(row);
		setOpenPanel(type);
	};

	const closePanel = () => setOpenPanel(null);

	const onUpdateLevel = (row: CompetencyAssignmentJSON) => {
		// Close any open panel first — actionSelectedRow backs both the panels (via remount key) and
		// the modal, so opening the modal on a different row must not swap an open panel's content.
		setOpenPanel(null);
		setActionSelectedRow(row);
		setIsUpdateModalOpen(true);
	};

	const closeUpdateModal = () => setIsUpdateModalOpen(false);

	return {
		openPanel,
		actionSelectedRow,
		isUpdateModalOpen,
		onOpenPanel,
		closePanel,
		onUpdateLevel,
		closeUpdateModal,
	};
};

export default useCompetencyAssignmentActions;
